import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Upload, Search, ImageIcon, Loader2, Check, Sparkles, Camera } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { AIImagePanel } from './AIImagePanel';
import { PaisleyPhotoPanel } from './PaisleyPhotoPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Media library picker.
 * - Single mode (default): clicking an image calls `onSelect(url)` and closes.
 * - Multi mode (`multi={true}`): clicks toggle selection; "Insert N images"
 *   button at the bottom calls `onSelect([url1, url2, ...])` then closes.
 */
export const MediaPicker = ({ open, onClose, onSelect, title = 'Select an image', multi = false, aiContext = null }) => {
  const { toast } = useToast();
  const [tab, setTab] = useState('library');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState('');
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState([]);       // ordered list of urls (multi mode)
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const limit = 24;

  const absolute = (url) => (url && url.startsWith('http') ? url : `${BACKEND_URL}${url || ''}`);

  const fetchPage = useCallback(async (nextSkip = 0, nextQ = q) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/media`, {
        params: { q: nextQ || undefined, skip: nextSkip, limit },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(prev => (nextSkip === 0 ? res.data.items : [...prev, ...res.data.items]));
      setHasMore(res.data.has_more);
      setSkip(nextSkip);
    } catch (e) {
      toast({ title: 'Failed to load library', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [q, toast]);

  useEffect(() => {
    if (open) {
      setQ('');
      setSelected([]);
      setTab('library');
      fetchPage(0, '');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchPage(0, q), 300);
    return () => clearTimeout(t);
  }, [q, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (list.length === 1 && !multi) {
        // Single upload (single-select mode) — use the existing /api/upload endpoint
        const fd = new FormData();
        fd.append('file', list[0]);
        const res = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        const url = absolute(res.data.url);
        toast({ title: 'Uploaded', description: 'Image added to library' });
        onSelect(url);
        onClose();
      } else {
        // Bulk upload — /api/admin/upload-bulk, then refresh library.
        // In multi mode we ALSO auto-add the newly uploaded images to the selection.
        const fd = new FormData();
        list.forEach((f) => fd.append('files', f));
        const res = await axios.post(`${BACKEND_URL}/api/admin/upload-bulk`, fd, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        const { uploaded, errors, items: uploadedItems } = res.data;
        toast({
          title: `${uploaded} image${uploaded === 1 ? '' : 's'} uploaded`,
          description: errors.length ? `${errors.length} failed — check console.` : 'Refreshed library.',
        });
        if (errors.length) console.warn('Bulk upload errors:', errors);
        if (multi && Array.isArray(uploadedItems)) {
          const newUrls = uploadedItems.map((it) => absolute(it.url));
          setSelected((s) => [...s, ...newUrls.filter((u) => !s.includes(u))]);
        }
        await fetchPage(0, q);
      }
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const toggleSelected = (url) => {
    setSelected((s) => (s.includes(url) ? s.filter((u) => u !== url) : [...s, url]));
  };

  const confirmMulti = () => {
    if (selected.length === 0) return;
    onSelect(selected);
    onClose();
  };

  // A generated image behaves exactly like one picked from the library.
  const useGenerated = (url) => {
    if (multi) {
      setSelected((s) => (s.includes(url) ? s : [...s, url]));
      setTab('library');
      fetchPage(0, q);
    } else {
      onSelect(url);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-picker-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="media-picker-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ImageIcon size={20} className="text-cyan-500" />
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="media-picker-close">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 border-b border-gray-100" data-testid="media-picker-tabs">
          {[
            { key: 'library', label: 'Library' },
            { key: 'photos', label: 'Paisley Photos' },
            { key: 'ai', label: 'Generate with AI' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`media-tab-${t.key}`}
              className={
                'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ' +
                (tab === t.key
                  ? 'border-cyan-500 text-cyan-700'
                  : 'border-transparent text-slate-500 hover:text-slate-900')
              }
            >
              {t.key === 'ai' && <Sparkles size={14} />}
              {t.key === 'photos' && <Camera size={14} />} {t.label}
            </button>
          ))}
        </div>

        {tab === 'ai' ? (
          <div className="flex-1 overflow-y-auto">
            <AIImagePanel context={aiContext || {}} onUse={useGenerated} />
          </div>
        ) : tab === 'photos' ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            <PaisleyPhotoPanel onUse={useGenerated} />
          </div>
        ) : (
        <>
        {/* Toolbar */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-100 bg-slate-50">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              ref={inputRef}
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by filename or post title — e.g. 'town hall'"
              className="pl-9"
              data-testid="media-search-input"
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => {
              if (e.target.files?.length) handleUpload(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="shrink-0"
            data-testid="media-upload-new-btn"
          >
            {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
            {uploading ? 'Uploading…' : 'Upload (single or bulk)'}
          </Button>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4" data-testid="media-grid">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="animate-spin mr-2" size={20} /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <ImageIcon size={32} className="mx-auto mb-3 opacity-40" />
              <p>No images found{q ? ` for "${q}"` : ''}.</p>
              <p className="text-xs mt-1">Try a different search, or upload a new image.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {items.map((item, idx) => {
                  const url = absolute(item.url);
                  const isSelected = multi && selected.includes(url);
                  const onTile = () => {
                    if (multi) toggleSelected(url);
                    else { onSelect(url); onClose(); }
                  };
                  return (
                    <button
                      type="button"
                      key={`${item.url}-${idx}`}
                      onClick={onTile}
                      className={`group relative aspect-square rounded-lg overflow-hidden border-2 transition-all focus:outline-none ${
                        isSelected
                          ? 'border-cyan-500 ring-2 ring-cyan-300'
                          : 'border-transparent hover:border-cyan-500 focus:border-cyan-500'
                      }`}
                      title={item.title || item.filename}
                      data-testid="media-item"
                      aria-pressed={isSelected || undefined}
                    >
                      <img
                        src={url}
                        alt={item.title || item.filename}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => { e.target.style.opacity = '0.2'; }}
                        loading="lazy"
                      />
                      {isSelected && (
                        <>
                          <div className="absolute inset-0 bg-cyan-500/25" />
                          <div className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-500 text-white shadow-md">
                            <Check size={14} />
                          </div>
                          <div className="absolute top-1.5 left-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-black/75 text-white text-[10px] font-bold">
                            {selected.indexOf(url) + 1}
                          </div>
                        </>
                      )}
                      {item.title && !isSelected && (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-white text-[10px] leading-tight line-clamp-2">{item.title}</p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {hasMore && (
                <div className="flex justify-center mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fetchPage(skip + limit, q)}
                    disabled={loading}
                    data-testid="media-load-more-btn"
                  >
                    {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
        </>
        )}

        {/* Selected strip (multi mode only) — drag to reorder */}
        {multi && selected.length > 0 && (
          <div
            className="border-t border-gray-200 bg-white px-5 py-3 overflow-x-auto"
            data-testid="media-picker-selected-strip"
          >
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              Insertion order — drag to reorder
            </p>
            <div className="flex items-center gap-2">
              {selected.map((url, idx) => (
                <div
                  key={url}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
                    if (Number.isNaN(from) || from === idx) return;
                    setSelected((s) => {
                      const next = s.slice();
                      const [moved] = next.splice(from, 1);
                      next.splice(idx, 0, moved);
                      return next;
                    });
                  }}
                  className="group relative shrink-0 w-16 h-16 rounded-md overflow-hidden ring-2 ring-cyan-400 cursor-grab active:cursor-grabbing"
                  data-testid={`selected-thumb-${idx}`}
                  title={`Position ${idx + 1} — drag to reorder`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover pointer-events-none" />
                  <span className="absolute top-0.5 left-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/75 text-white text-[10px] font-bold pointer-events-none">
                    {idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleSelected(url)}
                    className="absolute top-0.5 right-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/95 text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove from selection"
                    data-testid={`selected-thumb-remove-${idx}`}
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer (multi mode only) */}
        {multi && (
          <div className="border-t border-gray-200 bg-slate-50 px-5 py-3 flex items-center justify-between" data-testid="media-picker-multi-footer">
            <div className="text-sm text-slate-600">
              {selected.length === 0
                ? 'Click images to add them to the gallery'
                : `${selected.length} image${selected.length === 1 ? '' : 's'} selected`}
              {selected.length > 1 && (
                <button
                  type="button"
                  onClick={() => setSelected([])}
                  className="ml-3 text-xs text-slate-500 underline hover:text-slate-800"
                  data-testid="media-picker-clear-selection"
                >
                  clear
                </button>
              )}
            </div>
            <Button
              type="button"
              onClick={confirmMulti}
              disabled={selected.length === 0}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="media-picker-insert-gallery-btn"
            >
              Insert {selected.length || ''} image{selected.length === 1 ? '' : 's'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaPicker;
